import { EditIcon } from '@chakra-ui/icons'
import { Box, Collapse, Flex, IconButton, Text } from '@chakra-ui/react'
import {
  assessmentOnCurrentPlanNote,
  assessmentOnPartnerPlanNote,
  confirmPartnerIndividualNote,
  formatField,
  multipleCoverageOptionsDescriptionField,
  multipleCoverageOptionsField,
} from '@hb/shared'
import { useFormWizard } from '@hb/shared-frontend'
import { useMemo } from 'react'
import { useInsuranceInfoStage } from './context'
import { InsuranceInfoForm } from './InsuranceInfoForm'
import { InsuranceInfoNoteView } from './InsuranceInfoNoteView'

const ConfirmPartnerIndividualNote = ({ isComplete }: { isComplete: boolean }) => {
  const { insuranceInfoData } = useInsuranceInfoStage()

  const isActive = useMemo(
    () =>
      isComplete &&
      insuranceInfoData.option === 'multiple-options' &&
      insuranceInfoData.multipleOptionsDescription === 'partner-individual' &&
      insuranceInfoData.confirmPartnerPlan === true,
    [insuranceInfoData, isComplete],
  )
  return <InsuranceInfoNoteView isActive={isActive} stage={confirmPartnerIndividualNote} />
}

const AssessmentOnPartnerPlanNote = ({ isComplete }: { isComplete: boolean }) => {
  const { insuranceInfoData } = useInsuranceInfoStage()
  const isActive = useMemo(
    () =>
      isComplete &&
      insuranceInfoData.option === 'multiple-options' &&
      insuranceInfoData.multipleOptionsDescription === 'partner-employer' &&
      insuranceInfoData.planToAssess === 'partner-plan',
    [insuranceInfoData, isComplete],
  )
  return <InsuranceInfoNoteView isActive={isActive} stage={assessmentOnPartnerPlanNote} />
}

const AssessmentOnCurrentPlanNote = ({ isComplete }: { isComplete: boolean }) => {
  const { insuranceInfoData } = useInsuranceInfoStage()
  const isActive = useMemo(
    () =>
      isComplete &&
      insuranceInfoData.option === 'multiple-options' &&
      insuranceInfoData.multipleOptionsDescription === 'partner-employer' &&
      insuranceInfoData.planToAssess === 'current-plan',
    [insuranceInfoData, isComplete],
  )
  return <InsuranceInfoNoteView isActive={isActive} stage={assessmentOnCurrentPlanNote} />
}
const MultipleCoverageOptionsComplete = ({
  isComplete,
  onEdit,
}: {
  isComplete: boolean
  onEdit: () => void
}) => {
  const { readOnly } = useFormWizard()
  const { insuranceInfoData } = useInsuranceInfoStage()
  const multipleCoverageDescription = useMemo(
    () =>
      formatField(
        multipleCoverageOptionsDescriptionField,
        insuranceInfoData.multipleOptionsDescription,
      ),
    [insuranceInfoData],
  )

  const subText = useMemo(() => {
    const option = insuranceInfoData.multipleOptionsDescription

    switch (option) {
      case 'partner-individual':
        return insuranceInfoData.confirmPartnerPlan
          ? 'I have confirmed that I am covered by this plan'
          : 'Since you are not covered by this plan, please choose another option for the first question'
      case 'partner-employer':
        if (!insuranceInfoData.planToAssess) return ''
        return insuranceInfoData.planToAssess === 'current-plan'
          ? 'I want an assessment on my current plan'
          : 'I want an assessment on my partner/ family member’s plan'
      default:
        return ''
    }
  }, [insuranceInfoData])

  return (
    <>
      <Collapse style={{ width: '100%' }} in={isComplete}>
        <Flex pt={2} gap={1.5} width="100%">
          <Flex gap={2} flexFlow="column" flex={1} minW={0}>
            <Flex
              borderRadius={4}
              bg="white"
              border="1px solid #cdcdcd"
              py={1}
              px={3}
              flexFlow="column"
              flex={1}
              minW={0}>
              <Text fontSize="lg">{multipleCoverageDescription}</Text>
              <Text fontSize="lg" fontWeight={600}>
                {subText}
              </Text>
            </Flex>
          </Flex>
          {!readOnly ? (
            <IconButton
              borderRadius="full"
              onClick={onEdit}
              icon={<EditIcon />}
              aria-label="Edit"
            />
          ) : null}
        </Flex>
      </Collapse>
      <ConfirmPartnerIndividualNote isComplete={isComplete} />
      <AssessmentOnCurrentPlanNote isComplete={isComplete} />
      <AssessmentOnPartnerPlanNote isComplete={isComplete} />
    </>
  )
}

export const MultipleCoverageOptions = () => {
  const { insuranceInfoData, onUpdate } = useInsuranceInfoStage()
  const isComplete = useMemo(() => {
    if (!insuranceInfoData.option) return false
    if (insuranceInfoData.option !== 'multiple-options') return false
    const option = insuranceInfoData.multipleOptionsDescription
    if (!option) return false
    switch (option) {
      case 'partner-individual':
        return !!insuranceInfoData.confirmPartnerPlan
      case 'partner-employer':
        return !!insuranceInfoData.planToAssess
    }
    return false
  }, [insuranceInfoData])
  const isActive = useMemo(
    () => !isComplete && insuranceInfoData.option === 'multiple-options',
    [insuranceInfoData, isComplete],
  )

  return (
    <Collapse style={{ width: '100%' }} in={insuranceInfoData.option === 'multiple-options'}>
      <Box w="100%" pt={2}>
        <InsuranceInfoForm
          field={multipleCoverageOptionsField}
          data={insuranceInfoData}
          onSubmit={onUpdate}
          isActive={isActive}
        />
        <MultipleCoverageOptionsComplete
          isComplete={isComplete}
          onEdit={() => onUpdate({ multipleOptionsDescription: null })}
        />
      </Box>
    </Collapse>
  )
}
